// @flow

// Looking for the top level of PinRep? This is it!
// ...unless you need impressions tracking, in which case you should check out PinRepWithImpressions.

// PinRepConfiguration is a transforming layer, taking _descriptive_ props from the grid level and
// passing down _imperative_ props to PinRepContainer. Think "where is this PinRep being displayed?"
// into "what elements are displayed, and where/how?".

// Note the difference between "{feature}Options" that are passed into/through this component,
// and the "{feature}Props" that this component outputs. Options are provided by the developer;
// they inform, but don't dictate, what will be shown. Props are derived from these options and
// other logic, and *do* dictate what will be shown.

/****** Need to add a prop to PinRep? In most cases: please don't! ******/
// The vast majority of variations in the PinRep can be derived from data on the `pin` object and
// the surface name. There are a handful of cases where this is not possible. These are where
// the "{feature}Options" come into play. Adding new Options should be avoided if possible.

// Architecture:
// PinRepConfiguration: accepts descriptive props, transforms to imperative props
//   PinRepContainer: handles hover visibility, routes imperative props to child components
//     PinRepPresentation: handles layout with slots for possible child components, wraps PinVisual

import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import buildActionBarProps from 'app/common/react/components/PinRep/configUtils/buildActionBarProps';
import buildPinVisualProps from 'app/common/react/components/PinRep/configUtils/buildPinVisualProps';
import buildPrimaryContentProps from 'app/common/react/components/PinRep/configUtils/buildPrimaryContentProps';
import buildSecondaryContentProps from 'app/common/react/components/PinRep/configUtils/buildSecondaryContentProps';
import doesViewerOwnBoard from 'app/common/react/components/PinRep/configUtils/doesViewerOwnBoard';
import ErrorBoundary from 'app/packages/error-boundary';
import getDenseViewState from 'app/common/react/components/PinRep/configUtils/getDenseViewState';
import getFeedbackType from 'app/common/react/components/PinRep/utils/getFeedbackType';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import isCarouselPin from 'app/common/react/components/common/Carousel/utils/isCarouselPin';
import isProductCarousel from 'app/common/react/components/common/Carousel/utils/isProductCarousel';
import isRemovablePin from 'app/common/lib/util/isRemovablePin';
import PinRepContainer from 'app/common/react/components/PinRep/core/PinRepContainer';
import { filterProps } from 'app/common/lib/util/ObjectUtils';
import { isVideoPlaying } from 'app/common/lib/util/PinUtils';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { TrackingParametersContextProvider } from 'app/common/react/components/PinRep/TrackingParametersContext';
import { type AttributionOption } from 'app/common/react/components/PinRep/utils/getAttributionType';
import { type DetailedBoard } from 'app/packages/pinterest-flow-types/board';
import { type FeedbackOptions } from 'app/common/react/components/PinRep/feedback-types';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type Resolution } from 'app/common/react/components/PinRep/components/PinVisual/PinVisual';
import { type SaveButtonOptions } from 'app/common/react/components/PinRep/components/PinRepPrimaryContent';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import { type State as ViewConfig } from 'app/common/redux/reducers/ui/views';
import { type Viewer } from 'app/packages/pinterest-flow-types/viewer';
import { useExperiment } from 'app/common/react/components/experiments/useExperiment';
import { useRequestContext } from 'app/common/react/components/RequestContext';
import buildFooterProps, {
  type BoardActivityOptions,
} from 'app/common/react/components/PinRep/configUtils/buildFooterProps';
import {
  debugPinImpressionsEnabled,
  type DebugImpressionState,
} from 'app/common/react/components/PinRep/components/DebugPinImpressions';
import { isNewUser, isResurrectedUser } from 'app/common/lib/ActivationUtil';

// Every pin should have a defined surface prop describing the displaying grid.
// This is used to determine surface-specific logic without the need to pass in separate props.
export type Surface =
  | 'ArticleBlock'
  | 'ArticleProductsFeed'
  | 'AuthHomefeed'
  | 'AuthLandingPageGrid'
  | 'BaseBoardPinGrid'
  | 'BoardActivityThreadItem'
  | 'BoardCreateUpsell'
  | 'BoardMoreIdeasTabContent'
  | 'BrioCloseupRelatedProducts'
  | 'CategoryPage'
  | 'CloseupRelatedProducts'
  | 'Collage'
  | 'DomainFeedPage'
  | 'EngagementDetails'
  | 'ExplorePossibilities'
  | 'ExplorePossibilitiesPersistent'
  | 'ExploreGrid'
  | 'FlashlightResultGrid'
  | 'FollowingFeedGrid'
  | 'HomefeedMoreIdeasTabs'
  | 'IdeasCardFeedPage'
  | 'ImagesFeedPage'
  | 'InterestPage'
  | 'NewsPage'
  | 'PinLandingRelatedPinGrid'
  | 'RelatedPinGrid'
  | 'RelatedProductsFeed'
  | 'ProductPinsFeed'
  | 'SearchItem'
  | 'SearchItemPersistentOneClickSave'
  | 'ShoppingPackageItem'
  | 'STLProductsFeed'
  | 'TopicPage'
  | 'UpdatedCloseupRelatedProducts'
  | 'UserProfilePinGrid';

export type TrackingParameters = {|
  // ImpressionTracker needs the following props. It's possible that
  // PinRepContainer also uses them, so all of them are passed to
  // PinRepContainer in addition to ImpressionTracker.
  componentType?: $Values<PCONST.thrift.ComponentType>,
  contextLogData?: Object,
  impressionAuxFields?: Object,
  slotIndex: number,
  viewData?: {
    pin_id?: string,
  },
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

type ReduxStateProps = {|
  // Board this pin is on, if applicable
  board?: DetailedBoard,

  // If this pin is a video and it is currently playing, currentlyPlayingVideo
  // will be true. False for non-video or non-playing.
  currentlyPlayingVideo: boolean,

  isSocialSeasonalBoard: boolean,

  // PinRepConfiguration is normalized, so it always gets Pin data from Redux based
  // on the specified pinId.
  reduxPin: Pin,

  // Config pertaining to dense view, if applicable
  viewConfig: ViewConfig,

  // We pull a bunch of props off viewer, so it's easiest to just import the
  // whole thing.
  viewer: Viewer,
|};

// Please don't add props here unless absolutely necessary! See note above at the top of the file.
export type OwnProps = {|
  pinId: string,
  // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
  pin: Pin,

  /*** Grid-level options ***/
  // These options are provided at the grid level to customize the PinRep. They are generally
  // respected, but can be overridden in certain situations (like NUX).

  // Giraffe Pins can take up a ton of room in feeds, so we add crop ratios on
  // the backend and crop by default. Certain feeds may want to preserve the
  // full Pin image, so this prop allows explicit enabling/disabling.
  allowImageCropping?: boolean,

  // Option to show/hide attributions over the usual logic. See ExperimentalPinRep/utils/getAttributionType
  // for more details about how this can/will be overridden
  attributionOption?: ?AttributionOption,

  // for overriding click to closeup and other click targets on pin rep
  blockClickEvents?: boolean,

  // For changes to default board activity behavior
  boardActivityOptions?: BoardActivityOptions,

  // Control feedback text/options in the overflow menu.
  feedbackOptions?: FeedbackOptions,

  // Option to select "image only" PinRep appearance. This is typically derived from NUX status;
  // this option is available to override that logic if necessary.
  imageOnlyOption: 'image-only' | 'default' | 'not-image-only',

  // PinVisual resolution (pass-through prop):
  resolution: Resolution,

  // Type of save button shown, and associated options
  saveButtonOptions?: SaveButtonOptions,

  // Where is this PinRep being shown?
  surface: Surface,

  /*** Other props ***/
  // Used while debugging impressions tracking to indicate current tracking state
  debugImpressionState: DebugImpressionState,

  // Callbacks on interaction:
  onError?: () => void,
  onLoad?: () => void,

  // Props needed for impressions and other tracking. Mostly used by ImpressionsTracker, but also
  // by some child components (available via Context).
  trackingParameters: TrackingParameters,
|};

export type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
|};

export function PinRepConfiguration({
  allowImageCropping,
  blockClickEvents,
  attributionOption: attributionOptionProp,
  board,
  boardActivityOptions,
  currentlyPlayingVideo,
  debugImpressionState,
  feedbackOptions,
  imageOnlyOption,
  isSocialSeasonalBoard,
  reduxPin: pin,
  resolution,
  saveButtonOptions,
  surface,
  trackingParameters,
  viewConfig,
  viewer,
  ...pinRepContainerProps
}: Props) {
  const {
    // This is only true if viewer does *not* own board, but is a collaborator
    collaborated_by_me: collaboratedByMe,
    // This count does *not* include the owner of the board
    collaborator_count: collaboratorCount,
    // This will be false if the board was collaborative, but all collaborators were removed/left
    is_collaborative: isCollaborative,
  } = board || {};
  const { pinner, showFeedback, is_quick_promotable: isQuickPromotable } = pin;
  const isRemovable = isRemovablePin(pin);

  const { slotIndex, viewParameter } = trackingParameters;
  const requestContext = useRequestContext();

  const inChangeTitlesExp =
    new SingleTreatmentExp(requestContext, 'web_unified_description_both').enabled() ||
    new SingleTreatmentExp(
      requestContext,
      'web_unified_description_both_perf',
    ).enabledWithNoActivation();

  // Display multi-product-image pin  as carousel or not.
  const allowProductCarousel = isProductCarousel(pin)
    ? new SingleTreatmentExp(requestContext, 'web_carousel_product_pin').enabled()
    : false;
  const hasCarouselData = isCarouselPin(pin) || allowProductCarousel;

  // Lego grid change experiment
  const { anyEnabled: isLegoGroupBoardsEnabled } = useExperiment(
    'gb_web_lego_grid_change',
    board && board.is_collaborative,
  );
  const showAvatarAndNameOnLeft = !!board && board.is_collaborative && isLegoGroupBoardsEnabled;
  const actionBarEditBeforeShare = !!board && board.is_collaborative && isLegoGroupBoardsEnabled;

  // Get viewer info.
  const viewerIsEmployee = viewer.isAuth && viewer.isEmployee;
  const viewerIsNewOrResurrectedNonPartner =
    viewer.isAuth &&
    (isNewUser({
      user: viewer,
    }) ||
      isResurrectedUser({ user: viewer })) &&
    !viewer.isPartner;

  // Resolve passed-in imageOnlyOption and attributionOption if user is NUX
  let showImageOnly = viewerIsNewOrResurrectedNonPartner;
  if (imageOnlyOption !== 'default') {
    showImageOnly = imageOnlyOption === 'image-only';
  }
  let attributionOption = attributionOptionProp;
  if (viewerIsNewOrResurrectedNonPartner) {
    attributionOption = 'image-only';
  }

  // Feedback props are used throughout various child components
  const feedbackType = getFeedbackType({ feedbackOptions, pin, viewParameter });
  const { data: feedbackData } = feedbackOptions || {};

  // Profile/board-specific
  const hasCollaborators = collaboratorCount > 0 || isCollaborative;
  const isDenseView = getDenseViewState(viewConfig, surface);
  const isGroupBoard = hasCollaborators && surface === 'BaseBoardPinGrid';
  const isOwnBoard = doesViewerOwnBoard(board, viewer);
  const isOwnPin = Boolean(viewer.isAuth && pinner && pinner.id === viewer.id);
  const viewerIsCollaborator = collaboratedByMe || (hasCollaborators && isOwnBoard);
  const viewerIsPartner = viewer.isAuth && viewer.isPartner;

  // PRIMARY CONTENT (save button, etc)
  const showPrimaryAndSecondaryContent = !['EngagementDetails', 'BoardCreateUpsell'].includes(
    surface,
  );

  const primaryContentProps = useMemo(
    () =>
      buildPrimaryContentProps({
        isDenseView,
        isGroupBoard,
        isOwnBoard,
        isOwnPin,
        isQuickPromotable,
        isRemovable,
        saveButtonOptions,
        surface,
        viewer,
        viewerIsCollaborator,
        board,
      }),
    [
      isGroupBoard,
      isOwnBoard,
      isOwnPin,
      isQuickPromotable,
      isRemovable,
      saveButtonOptions,
      surface,
      viewer,
      board,
    ],
  );

  // SECONDARY CONTENT (pin type indentifier, etc)
  const secondaryContentProps = useMemo(
    () => buildSecondaryContentProps({ allowProductCarousel, surface }),
    [allowProductCarousel, surface],
  );

  // ACCESSORY CONTENT (carousel sliders, etc)
  const showAccessoryContent = hasCarouselData;

  // ACTION BAR
  const showActionBar = !['EngagementDetails', 'BoardCreateUpsell'].includes(surface);

  const actionBarProps = useMemo(
    () =>
      buildActionBarProps({
        actionBarEditBeforeShare,
        feedbackData,
        feedbackType,
        inChangeTitlesExp,
        isDenseView,
        isGroupBoard,
        isOwnBoard,
        pin,
        surface,
      }),
    [feedbackData, feedbackType, isDenseView, isGroupBoard, isOwnBoard, pin, surface],
  );

  // FOOTER

  // Footer group board comment experiment
  const { anyEnabled: showBoardActivityComments } = useExperiment(
    'gb_web_exposed_comment',
    board && board.is_collaborative,
  );

  const footerProps = useMemo(
    () =>
      buildFooterProps({
        attributionOption,
        board,
        boardActivityOptions,
        feedbackData,
        feedbackType,
        hasCarouselData,
        inChangeTitlesExp,
        isGroupBoard,
        isLegoGroupBoardsEnabled,
        isOwnBoard,
        pin,
        showAvatarAndNameOnLeft,
        showBoardActivityComments,
        showImageOnly,
        surface,
        viewerIsPartner,
      }),
    [
      attributionOption,
      board,
      boardActivityOptions,
      feedbackData,
      feedbackType,
      hasCarouselData,
      inChangeTitlesExp,
      isGroupBoard,
      isLegoGroupBoardsEnabled,
      isOwnBoard,
      pin,
      showAvatarAndNameOnLeft,
      showBoardActivityComments,
      showImageOnly,
      surface,
      viewerIsPartner,
    ],
  );

  const hasFooterContent =
    Boolean(footerProps.pinTitle) ||
    footerProps.userNote ||
    footerProps.showSource ||
    footerProps.attributionType !== 'none';

  // Footer will always have an avatar image on group boards, but never a footer in dense view
  let showFooter = hasFooterContent || isGroupBoard;
  if (surface === 'EngagementDetails') {
    showFooter = false;
  } else if (isDenseView) {
    showFooter = false;
  } else if (isSocialSeasonalBoard) {
    showFooter = false;
  }

  // OBSCURED PIN OVERLAY
  const showObscuredPinOverlay = Boolean(showFeedback);

  // Promoted pins that show up on boards have special color treatment.
  // The background of the pin is light gray and 4px of padding is added to the pin
  const { isPromoted } = getPinTypes(pin);
  const borderOnPromotedPinInBoardExp = new SingleTreatmentExp(
    requestContext,
    'web_others_board_feed_ads_border',
  );
  const presentationProps = {
    showPromotedPinBorderTreatment:
      surface === 'BaseBoardPinGrid' && isPromoted && borderOnPromotedPinInBoardExp.enabled(),
  };

  // note this isn't being activated
  const isHomefeedControlPanelNagExp = new SingleTreatmentExp(
    requestContext,
    'web_homefeed_control_panel_nag',
  ).enabledWithNoActivation();
  const homefeedControlPanelHeaderExp = new SingleTreatmentExp(
    requestContext,
    'web_homefeed_control_panel_pin_feedback',
  );

  const obscuredPinOverlayProps = showObscuredPinOverlay
    ? {
        feedbackType,
        isHomefeedControlPanelExp:
          isHomefeedControlPanelNagExp || homefeedControlPanelHeaderExp.enabled(),
      }
    : undefined;

  // PIN VISUAL
  const showPromotedVideos = new SingleTreatmentExp(
    requestContext,
    'ads_desktop_video_ads',
  ).enabledWithNoActivation();

  const pinVisualProps = useMemo(
    () =>
      buildPinVisualProps({
        allowImageCropping,
        hasCarouselData,
        isSocialSeasonalBoard,
        resolution,
        showPromotedVideos,
        surface,
      }),
    [
      allowImageCropping,
      hasCarouselData,
      isSocialSeasonalBoard,
      resolution,
      showPromotedVideos,
      surface,
    ],
  );

  // IMPRESSIONS DEBUGGER
  const showImpressionsDebugger = viewerIsEmployee && debugPinImpressionsEnabled();
  const impressionsDebuggerProps = useMemo(() => ({ debugImpressionState, slotIndex }), [
    debugImpressionState,
    slotIndex,
  ]);

  return (
    <ErrorBoundary name="PinRepConfiguration">
      <TrackingParametersContextProvider value={trackingParameters}>
        <PinRepContainer
          actionBarProps={actionBarProps}
          blockClickEvents={blockClickEvents}
          currentlyPlayingVideo={currentlyPlayingVideo}
          footerProps={footerProps}
          forcePrimaryContentVisibility={[
            'ExplorePossibilitiesPersistent',
            'HomefeedMoreIdeasTabs',
            'SearchItemPersistentOneClickSave',
          ].includes(surface)}
          impressionsDebuggerProps={impressionsDebuggerProps}
          obscuredPinOverlayProps={obscuredPinOverlayProps}
          pin={pin}
          pinVisualProps={pinVisualProps}
          presentationProps={presentationProps}
          primaryContentProps={primaryContentProps}
          secondaryContentProps={secondaryContentProps}
          showPrimaryAndSecondaryContent={showPrimaryAndSecondaryContent}
          showAccessoryContent={showAccessoryContent}
          showActionBar={showActionBar}
          showFooter={showFooter}
          showImpressionsDebugger={showImpressionsDebugger}
          showSignalDebugger={viewerIsEmployee}
          showObscuredPinOverlay={showObscuredPinOverlay}
          // Necessary to filter out pass-thru props that PinRepContainer isn't expecting
          {...filterProps(pinRepContainerProps, ['pinId'])}
          viewType={trackingParameters.viewType}
        />
      </TrackingParametersContextProvider>
    </ErrorBoundary>
  );
}

function mapStateToProps(
  {
    boards: { isSocialSeasonalBoard = false, content: boardsMap = {} },
    pins,
    ui,
    viewer,
  }: ReduxState,
  { pin: ownPin, pinId }: OwnProps,
): ReduxStateProps {
  // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
  const pin = pins[pinId] || ownPin;
  const pinBoardId = pin.board && pin.board.id;
  const reduxBoard = pinBoardId ? boardsMap[`${pinBoardId}`] : undefined;
  const board = reduxBoard || pin.board;

  const videosAutoplaying = ui.app
    ? ui.app.videosAutoplaying
    : {
        organicVideosAutoplaying: {},
        promotedVideosAutoplaying: {},
      };
  const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);

  return {
    // $FlowFixMe: coercing different board types together.
    board,
    currentlyPlayingVideo: isCurrentlyPlaying,
    isSocialSeasonalBoard,
    // $FlowFixMe: coercing different Pin types together.
    reduxPin: pin,
    viewConfig: ui.views,
    viewer,
  };
}

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  () => ({}),
)(PinRepConfiguration);
